import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3013/api";

// Theme configurations
const themes = {
  dark: {
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    gurmukhiColor: "#ffffff",
    translitColor: "#a0c4ff",
    translationColor: "#bde0fe",
    angColor: "#ffc971",
    dividerColor: "rgba(255,255,255,0.2)",
  },
  light: {
    background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #dee2e6 100%)",
    gurmukhiColor: "#1a1a2e",
    translitColor: "#495057",
    translationColor: "#6c757d",
    angColor: "#0d6efd",
    dividerColor: "rgba(0,0,0,0.1)",
  },
  saffron: {
    background: "linear-gradient(135deg, #1a1a2e 0%, #2d1b10 50%, #3d1f00 100%)",
    gurmukhiColor: "#ffd700",
    translitColor: "#ffb347",
    translationColor: "#f4a460",
    angColor: "#ff8c00",
    dividerColor: "rgba(255,215,0,0.3)",
  },
  custom: {
    background: "#000000",
    gurmukhiColor: "#ffffff",
    translitColor: "#a0c4ff",
    translationColor: "#bde0fe",
    angColor: "#ffc107",
    dividerColor: "rgba(255,255,255,0.2)",
  },
};

// Helper to adjust color brightness
const adjustColor = (color, amount) => {
  const clamp = (num) => Math.min(255, Math.max(0, num));
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const r = clamp((num >> 16) + amount);
  const g = clamp(((num >> 8) & 0x00FF) + amount);
  const b = clamp((num & 0x0000FF) + amount);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

// Font size configurations
const fontSizes = {
  small: { gurmukhi: 48, translit: 20, translation: 18 },
  medium: { gurmukhi: 64, translit: 24, translation: 22 },
  large: { gurmukhi: 80, translit: 28, translation: 26 },
  xlarge: { gurmukhi: 96, translit: 32, translation: 30 },
};

const GurbaniDisplay = () => {
  const [displayState, setDisplayState] = useState(null);
  const [currentLines, setCurrentLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pongTimeoutRef = useRef(null);

  // Get theme and font size from display state settings
  const theme = displayState?.settings?.theme || "dark";
  const fontSize = displayState?.settings?.fontSize || "large";
  const showTransliteration = displayState?.settings?.showTransliteration ?? true;
  const showTranslation = displayState?.settings?.showTranslation ?? true;
  const showAng = displayState?.settings?.showAng ?? true;
  const larivaar = displayState?.settings?.larivaar ?? false;
  const customMessage = displayState?.settings?.customMessage || "";
  const showCustomMessage = displayState?.settings?.showCustomMessage ?? false;
  
  // Custom colors
  const backgroundColor = displayState?.settings?.backgroundColor || "#000000";
  const textColor = displayState?.settings?.textColor || "#ffffff";
  const highlightColor = displayState?.settings?.highlightColor || "#ffc107";

  // Get theme styles - use custom colors if theme is 'custom'
  const getThemeStyles = () => {
    if (theme === 'custom') {
      return {
        background: backgroundColor,
        gurmukhiColor: textColor,
        translitColor: adjustColor(textColor, -40),
        translationColor: adjustColor(textColor, -60),
        angColor: highlightColor,
        dividerColor: `${textColor}33`,
      };
    }
    return themes[theme] || themes.dark;
  };
  
  const themeStyles = getThemeStyles();
  const sizes = fontSizes[fontSize] || fontSizes.large;

  // Process text for larivaar mode
  const processText = (text) => {
    if (!text) return "";
    if (larivaar) {
      return text.replace(/\s+/g, "");
    }
    return text;
  };

  // Fetch initial display status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/display/status`);
      if (response.data?.success) {
        setDisplayState(response.data.data);
        
        // If there's an active shabad, fetch its lines
        if (response.data.data?.currentShabadId && response.data.data?.mode !== "off") {
          const shabadResponse = await axios.get(
            `${API_URL}/shabads/${response.data.data.currentShabadId}`
          );
          if (shabadResponse.data?.success) {
            setCurrentLines(shabadResponse.data.data.lines || []);
          }
        }
      }
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch display status:", err);
      setError("Failed to connect to server");
      setLoading(false);
    }
  }, []);

  // Start ping interval to keep connection alive
  const startPingInterval = useCallback(() => {
    // Clear any existing interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (pongTimeoutRef.current) {
      clearTimeout(pongTimeoutRef.current);
    }

    // Send ping every 25 seconds (before typical 30s timeout)
    pingIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
          
          // Set timeout to check for pong response
          pongTimeoutRef.current = setTimeout(() => {
            console.log("No pong received, connection may be dead");
            // Force reconnect if no pong
            if (wsRef.current) {
              wsRef.current.close();
            }
          }, 5000);
        } catch (err) {
          console.error("Failed to send ping:", err);
        }
      }
    }, 25000);
  }, []);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Construct WebSocket URL - must match backend path /ws/
    const wsUrl = API_URL.replace(/^http/, "ws").replace("/api", "") + "/ws/";
    
    try {
      // Connect as display-only client (no auth required)
      wsRef.current = new WebSocket(wsUrl, ["display"]);

      wsRef.current.onopen = () => {
        console.log("WebSocket connected");
        setConnected(true);
        setError(null);
        // Start ping interval to keep connection alive
        startPingInterval();
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Handle pong response - clear the pong timeout
          if (message.type === "pong") {
            if (pongTimeoutRef.current) {
              clearTimeout(pongTimeoutRef.current);
              pongTimeoutRef.current = null;
            }
            return;
          }
          
          handleWebSocketMessage(message);
        } catch (err) {
          console.error("Failed to parse WebSocket message:", err);
        }
      };

      wsRef.current.onclose = () => {
        console.log("WebSocket disconnected");
        setConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Attempt to reconnect after 2 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log("Attempting to reconnect...");
          connectWebSocket();
        }, 2000);
      };

      wsRef.current.onerror = (err) => {
        console.error("WebSocket error:", err);
        setError("Connection error");
      };
    } catch (err) {
      console.error("Failed to create WebSocket:", err);
      // Retry connection after 2 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, 2000);
    }
  }, [startPingInterval]);

  // Handle WebSocket messages
  const handleWebSocketMessage = async (message) => {
    const { type, action, data } = message;

    switch (type) {
      case "display":
        if (action === "line_change") {
          setDisplayState((prev) => ({
            ...prev,
            currentLine: data.currentLine,
            currentLineIndex: data.currentLineIndex,
            currentShabadIndex: data.currentShabadIndex,
            totalLines: data.totalLines,
            mode: data.mode,
          }));
        } else if (action === "shabad_change" || action === "single_shabad" || action === "hukamnama") {
          // Fetch new shabad lines
          if (data.shabadId || data.shabad?.shabadId || data.displayState?.currentShabadId) {
            const shabadId = data.shabadId || data.shabad?.shabadId || data.displayState?.currentShabadId;
            try {
              const response = await axios.get(`${API_URL}/shabads/${shabadId}`);
              if (response.data?.success) {
                setCurrentLines(response.data.data.lines || []);
              }
            } catch (err) {
              console.error("Failed to fetch shabad:", err);
            }
          }
          if (data.displayState) {
            setDisplayState(data.displayState);
          }
          if (data.lines) {
            setCurrentLines(data.lines);
          }
        } else if (action === "off") {
          setDisplayState((prev) => ({ ...prev, mode: "off" }));
          setCurrentLines([]);
        } else if (action === "settings") {
          setDisplayState((prev) => ({
            ...prev,
            settings: { ...prev?.settings, ...data },
          }));
        }
        break;

      case "playlist":
        if (action === "activated") {
          setDisplayState(data.displayState);
          setCurrentLines(data.currentLines || []);
        } else if (action === "deactivated") {
          setDisplayState(data.displayState);
          setCurrentLines([]);
        }
        break;

      case "hukamnama":
        if (action === "set") {
          // Optionally refresh if currently showing Hukamnama
          if (displayState?.mode === "hukamnama") {
            fetchStatus();
          }
        }
        break;

      default:
        break;
    }
  };

  // Initial setup
  useEffect(() => {
    fetchStatus();
    connectWebSocket();

    return () => {
      // Cleanup all timers and connections
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (pongTimeoutRef.current) {
        clearTimeout(pongTimeoutRef.current);
      }
    };
  }, [fetchStatus, connectWebSocket]);

  // Render off state
  if (!displayState || displayState.mode === "off") {
    // Use custom colors even for off state if available
    const offBg = theme === 'custom' ? backgroundColor : themeStyles.background;
    const offText = theme === 'custom' ? textColor : themeStyles.gurmukhiColor;
    const offHighlight = theme === 'custom' ? highlightColor : themeStyles.angColor;
    
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          background: offBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Custom Message Overlay for off state too */}
        {showCustomMessage && customMessage && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.85)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 100,
              padding: 50,
            }}
          >
            <div
              style={{
                fontFamily: "'Noto Sans Gurmukhi', sans-serif",
                fontSize: 64,
                color: offHighlight,
                textAlign: "center",
                lineHeight: 1.8,
                maxWidth: "80%",
              }}
            >
              {customMessage}
            </div>
          </div>
        )}
        
        <div
          style={{
            fontSize: 120,
            marginBottom: 20,
            color: offHighlight,
          }}
        >
          ☬
        </div>
        <div
          style={{
            fontFamily: "'Noto Sans Gurmukhi', sans-serif",
            fontSize: 48,
            color: offText,
            textAlign: "center",
          }}
        >
          ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖਾਲਸਾ
        </div>
        <div
          style={{
            fontFamily: "'Noto Sans Gurmukhi', sans-serif",
            fontSize: 48,
            color: offText,
            textAlign: "center",
            marginTop: 10,
          }}
        >
          ਵਾਹਿਗੁਰੂ ਜੀ ਕੀ ਫਤਹਿ
        </div>
        
        {/* Google Font for Gurmukhi */}
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+Gurmukhi:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          background: themeStyles.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: themeStyles.gurmukhiColor,
          fontSize: 24,
        }}
      >
        Loading...
      </div>
    );
  }

  // Get current line
  const currentLineIndex = displayState.currentLineIndex || 0;
  const currentLine = displayState.currentLine || currentLines[currentLineIndex];

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        background: themeStyles.background,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Custom Message Overlay */}
      {showCustomMessage && customMessage && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
            padding: 50,
          }}
        >
          <div
            style={{
              fontFamily: "'Noto Sans Gurmukhi', sans-serif",
              fontSize: sizes.gurmukhi * 0.8,
              color: themeStyles.angColor,
              textAlign: "center",
              lineHeight: 1.8,
              maxWidth: "80%",
            }}
          >
            {customMessage}
          </div>
        </div>
      )}

      {/* Header with Ang info */}
      {showAng && (
        <div
          style={{
            padding: "10px 30px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${themeStyles.dividerColor}`,
          }}
        >
          <div
            style={{
              color: themeStyles.angColor,
              fontSize: 18,
              fontWeight: 500,
            }}
          >
            {displayState.mode === "hukamnama" ? "ਹੁਕਮਨਾਮਾ ਸਾਹਿਬ" : ""}
          </div>
          <div
            style={{
              color: themeStyles.angColor,
              fontSize: 18,
              fontWeight: 500,
            }}
          >
            ਅੰਗ {currentLine?.ang || displayState.currentLine?.ang || ""}
          </div>
          <div
            style={{
              color: themeStyles.translitColor,
              fontSize: 14,
            }}
          >
            {currentLineIndex + 1} / {displayState.totalLines || currentLines.length}
          </div>
        </div>
      )}

      {/* Main content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px 50px",
        }}
      >
        {/* Gurmukhi */}
        {currentLine?.gurmukhi && (
          <div
            style={{
              fontFamily: "'Noto Sans Gurmukhi', 'Mukta Mahee', sans-serif",
              fontSize: sizes.gurmukhi,
              color: themeStyles.gurmukhiColor,
              textAlign: "center",
              lineHeight: larivaar ? 1.4 : 1.8,
              marginBottom: 30,
              textShadow: theme === "dark" || theme === "saffron" || theme === "custom"
                ? "2px 2px 4px rgba(0,0,0,0.3)" 
                : "none",
              letterSpacing: larivaar ? "0.02em" : "normal",
            }}
          >
            {processText(currentLine.gurmukhi)}
          </div>
        )}

        {/* Transliteration */}
        {showTransliteration && currentLine?.transliteration && (
          <div
            style={{
              fontSize: sizes.translit,
              color: themeStyles.translitColor,
              textAlign: "center",
              fontStyle: "italic",
              marginBottom: 20,
            }}
          >
            {currentLine.transliteration}
          </div>
        )}

        {/* Punjabi Translation */}
        {showTranslation && currentLine?.translationPunjabi && (
          <div
            style={{
              fontFamily: "'Noto Sans Gurmukhi', 'Mukta Mahee', sans-serif",
              fontSize: sizes.translation + 4,
              color: themeStyles.angColor,
              textAlign: "center",
              maxWidth: "85%",
              marginBottom: 15,
              lineHeight: 1.6,
            }}
          >
            {currentLine.translationPunjabi}
          </div>
        )}

        {/* English Translation */}
        {showTranslation && currentLine?.translationEnglish && (
          <div
            style={{
              fontSize: sizes.translation,
              color: themeStyles.translationColor,
              textAlign: "center",
              maxWidth: "80%",
            }}
          >
            {currentLine.translationEnglish}
          </div>
        )}
      </div>

      {/* Footer with playlist info */}
      {displayState.mode === "playlist" && displayState.playlist && (
        <div
          style={{
            padding: "10px 30px",
            borderTop: `1px solid ${themeStyles.dividerColor}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: themeStyles.translitColor,
            fontSize: 14,
          }}
        >
          <span>{displayState.playlist.name}</span>
          <span>
            Shabad {(displayState.currentShabadIndex || 0) + 1} / {displayState.playlist.totalShabads}
          </span>
        </div>
      )}

      {/* Connection indicator */}
      <div
        style={{
          position: "absolute",
          bottom: 10,
          left: 10,
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: connected ? "#10b981" : "#ef4444",
        }}
        title={connected ? "Connected" : "Disconnected"}
      />

      {/* Google Font for Gurmukhi */}
      <link
        href="https://fonts.googleapis.com/css2?family=Noto+Sans+Gurmukhi:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
    </div>
  );
};

export default GurbaniDisplay;
